import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../../../services/api";
import networkApi from "../../network/api";
import {
  ReportCloudSandbox,
  RlCloudSandboxClassification
} from "../../samples_cloud/@types";
import { PieCountsDroppedFiles } from "../@types";
import useGraphPagination from "./useGraphPagination";
import { sampleReportKeys } from "../../samples/queryKeys";

const sortOrder = {
  MALICIOUS: 1,
  SUSPICIOUS: 2,
  NO_THREATS_FOUND: 3
};

// goodware = NO_THREATS_FOUND! TCB-16409
type MappedClassification = "no_threats_found" | "malicious" | "suspicious";

interface RlClassificationMapperFn {
  (
    classification: RlCloudSandboxClassification | undefined
  ): MappedClassification;
}

export const mapRlSandboxClassification: RlClassificationMapperFn =
  classification => {
    const classificationLower =
      classification?.toLowerCase() ?? "no_threats_found";
    return classificationLower as MappedClassification;
  };
const calculatePieData = (
  data: Array<{ classification: RlCloudSandboxClassification }> | undefined
) => {
  const stats = {
    no_threats_found: 0,
    malicious: 0,
    suspicious: 0
  };
  if (!data) return stats;

  data?.forEach(item => {
    const classification = mapRlSandboxClassification(item.classification);
    stats[classification] = (stats[classification] || 0) + 1;
  });

  return stats as PieCountsDroppedFiles;
};
const useInitRlSandboxData = (sha1: string) => {
  const { data: rlSandboxData } = useQuery({
    queryKey: sampleReportKeys.mergedReportKey(sha1, false),
    queryFn: () => api.rlSandboxMergedReport(sha1),
    select: ({ data }) => data as ReportCloudSandbox,
    enabled: Boolean(sha1)
  });

  const dynamicNetworkConnections = useMemo(
    () => [
      ...new Set([
        ...(rlSandboxData?.network?.tcp?.map(obj => obj.destination_ip) ?? []),
        ...(rlSandboxData?.network?.udp?.map(obj => obj.destination_ip) ?? []),
        ...(rlSandboxData?.network?.url?.map(obj => obj.url) ?? []),
        ...(rlSandboxData?.network?.http?.map(obj => obj.url) ?? []),
        ...(rlSandboxData?.network?.dns?.map(obj => obj.value) ?? [])
      ])
    ],
    [rlSandboxData?.network]
  );

  const {
    data: pagedDynamicNetworkConnections,
    goToNext: loadMoreDynamicNetworkConnections
  } = useGraphPagination(dynamicNetworkConnections, { pageSize: 20 });

  const { data: dynamicNetworkRepData, isFetching } = useQuery({
    queryKey: ["networkReputationDynamic", pagedDynamicNetworkConnections],
    queryFn: () => networkApi.urlReputation(pagedDynamicNetworkConnections),
    enabled: Boolean(pagedDynamicNetworkConnections?.length),
    select: ({ data }) => data.entries,
    refetchOnMount: false,
    refetchOnWindowFocus: false
  });
  const droppedFilesMemo = useMemo(
    () =>
      rlSandboxData?.dropped_files.sort(
        (a, b) =>
          sortOrder[a.classification] ||
          Infinity - sortOrder[b.classification] ||
          Infinity
      ) || [],
    [rlSandboxData?.dropped_files]
  );

  const pieCountsDroppedFiles = useMemo(
    () => calculatePieData(rlSandboxData?.dropped_files),
    [rlSandboxData?.dropped_files]
  );
  const { data: pagedDroppedFiles, goToNext: loadMoreDroppedFiles } =
    useGraphPagination(droppedFilesMemo, { pageSize: 20 });

  return {
    data: {
      dynamicNetworkRepData,
      pagedDroppedFiles,
      rlSandboxHistoryAnalysis: rlSandboxData?.history_analysis
    },
    counts: {
      totalDynamicNetworkConnections: dynamicNetworkConnections?.length ?? 0,
      totalDroppedFiles: rlSandboxData?.dropped_files.length ?? 0,
      pieCountsDroppedFiles
    },
    actions: { loadMoreDynamicNetworkConnections, loadMoreDroppedFiles },
    isFetching
  };
};

export default useInitRlSandboxData;
